import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const PU = process.env.PUBLIC_URL;

const capabilities = [
  {
    id: "cablelay",
    tab: "Cable Lay Software",
    title: "TSS Cable Lay Software",
    subtitle: "In-house engineering software",
    lead:
      "Software we developed in-house to plan and monitor a cable lay in real time.",
    points: [
      "Planning and simulation before the job",
      "Live monitoring during the lay",
      "Reporting and data export"
    ],
    tags: ["Real-time", "In-house build"],
    media: [
      {
        type: "video",
        src: PU + "/img/field/cablelay-01.mp4",
        poster: PU + "/img/field/cablelay-01-poster.jpg",
        caption: "Our cable lay software in operation"
      }
    ]
  },
  {
    id: "layrepair",
    tab: "Lay & Repair",
    title: "Cable Lay & Repair Support",
    subtitle: "Offshore and remote support",
    lead:
      "Our team supports installation and repair campaigns on board, with a remote desk following the same operation from shore.",
    points: [
      "Survey and charting personnel on board",
      "Remote monitoring from shore",
      "Daily progress reporting"
    ],
    tags: ["Onboard crew", "Remote support"],
    media: [
      {
        type: "image",
        src: PU + "/img/field/layrepair-01.jpg",
        caption: "Remote monitoring of deck operations"
      },
      {
        type: "image",
        src: PU + "/img/field/layrepair-02.jpg",
        caption: "Onboard charting station during a campaign"
      }
    ]
  },
  {
    id: "usv",
    tab: "USV Survey",
    title: "USV Bathymetry Survey",
    subtitle: "Unmanned Surface Vehicle",
    lead:
      "A compact unmanned vessel for survey work in shallow and congested water.",
    points: [
      "Shallow and nearshore coverage",
      "Runs the survey plan autonomously",
      "Quick to mobilise, launched from shore"
    ],
    tags: ["Unmanned", "Shallow water"],
    media: [
      {
        type: "image",
        src: PU + "/img/field/usv-01.jpg",
        caption: "USV deployed for a nearshore survey"
      },
      {
        type: "video",
        src: PU + "/img/field/usv-02.mp4",
        poster: PU + "/img/field/usv-02-poster.jpg",
        caption: "USV working in a busy nearshore area"
      },
      {
        type: "video",
        src: PU + "/img/field/usv-03.mp4",
        poster: PU + "/img/field/usv-03-poster.jpg",
        caption: "USV transiting to the survey area"
      }
    ]
  }
];

function FieldOps() {
  const [active, setActive] = useState(0);
  const [slide, setSlide] = useState(0);
  // Media opens in a full-screen lightbox. Nothing but the poster image is
  // downloaded until the visitor actually opens it.
  const [lightbox, setLightbox] = useState(false);

  const cap = capabilities[active];
  const media = cap.media;
  const total = media.length;
  const current = media[slide];
  const stillSrc = current.type === "video" ? current.poster : current.src;

  // Close the lightbox on Escape, and stop the page behind it from scrolling.
  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
    };
    document.addEventListener("keydown", onKey);
    document.body.classList.add("hidescroll");
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("hidescroll");
    };
  }, [lightbox]);

  function selectTab(index) {
    if (index === active) return;
    setActive(index);
    setSlide(0);
  }

  function showSlide(index) {
    if (index === slide) return;
    setSlide(index);
  }

  function go(step) {
    showSlide((slide + step + total) % total);
  }

  return (
    <motion.section
      id="field-ops"
      className="field-section"
      initial={{ opacity: 0, y: 75 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8 }}
    >
      <div className="container">
        <h2 className="main-title text-center">IN THE FIELD</h2>
        <p className="field-intro text-center">
          A closer look at three of our services.
        </p>

        <div className="field-tabs" role="tablist" aria-label="Capabilities">
          {capabilities.map((item, index) => (
            <button
              key={item.id}
              role="tab"
              type="button"
              id={`field-tab-${item.id}`}
              aria-selected={index === active}
              aria-controls={`field-panel-${item.id}`}
              className={`field-tab${index === active ? " is-active" : ""}`}
              onClick={() => selectTab(index)}
            >
              {item.tab}
            </button>
          ))}
        </div>

        <div
          className="field-body"
          role="tabpanel"
          id={`field-panel-${cap.id}`}
          aria-labelledby={`field-tab-${cap.id}`}
        >
          <div className="field-media">
            <div className="field-frame">
              <div
                className="field-frame-blur"
                style={{ backgroundImage: `url('${stillSrc}')` }}
                aria-hidden="true"
              />

              <AnimatePresence mode="wait">
                <motion.div
                  key={`${cap.id}-${slide}`}
                  className="field-stage"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <img
                    className="field-image"
                    src={stillSrc}
                    alt={current.caption}
                    loading="lazy"
                  />
                </motion.div>
              </AnimatePresence>

              {/* The frame only ever shows a still — the media opens full screen. */}
              <button
                type="button"
                className={current.type === "video" ? "field-play" : "field-play is-expand"}
                onClick={() => setLightbox(true)}
                aria-label={
                  current.type === "video"
                    ? `Play video: ${current.caption}`
                    : `Open full screen: ${current.caption}`
                }
              >
                {current.type === "video" ? (
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                ) : (
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                  </svg>
                )}
              </button>

              {total > 1 && (
                <>
                  <button
                    type="button"
                    className="field-arrow prev"
                    onClick={() => go(-1)}
                    aria-label="Previous media"
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M15 18l-6-6 6-6" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="field-arrow next"
                    onClick={() => go(1)}
                    aria-label="Next media"
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </button>

                  <span className="field-counter">
                    {slide + 1} / {total}
                  </span>
                </>
              )}

              {current.type === "video" && (
                <span className="field-badge">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                  Video
                </span>
              )}
            </div>

            <p className="field-caption">{current.caption}</p>

            {total > 1 && (
              <div className="field-thumbs">
                {media.map((item, index) => (
                  <button
                    key={item.src}
                    type="button"
                    className={`field-thumb${index === slide ? " is-active" : ""}`}
                    onClick={() => showSlide(index)}
                    aria-label={`Show media ${index + 1}`}
                    style={{
                      backgroundImage: `url('${item.type === "video" ? item.poster : item.src}')`
                    }}
                  >
                    {item.type === "video" && (
                      <span className="field-thumb-play">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={cap.id}
              className="field-copy"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35 }}
            >
              <span className="field-eyebrow">{cap.subtitle}</span>
              <h3 className="field-title">{cap.title}</h3>
              <p className="field-lead">{cap.lead}</p>
              <ul className="field-points">
                {cap.points.map((point) => (
                  <li key={point}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <div className="field-tags">
                {cap.tags.map((tag) => (
                  <span className="field-tag" key={tag}>{tag}</span>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            className="field-lightbox"
            onClick={() => setLightbox(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            role="dialog"
            aria-modal="true"
            aria-label={current.caption}
          >
            <button
              type="button"
              className="field-close"
              onClick={() => setLightbox(false)}
              aria-label="Close"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>

            <div className="field-lightbox-inner" onClick={(e) => e.stopPropagation()}>
              {current.type === "video" ? (
                <video
                  className="field-lightbox-media"
                  src={current.src}
                  poster={current.poster}
                  controls
                  autoPlay
                  playsInline
                  controlsList="nodownload"
                />
              ) : (
                <img
                  className="field-lightbox-media"
                  src={current.src}
                  alt={current.caption}
                />
              )}
              <p className="field-lightbox-caption">{current.caption}</p>
            </div>

            {total > 1 && (
              <>
                <button
                  type="button"
                  className="field-arrow prev in-lightbox"
                  onClick={(e) => {
                    e.stopPropagation();
                    go(-1);
                  }}
                  aria-label="Previous media"
                >
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="field-arrow next in-lightbox"
                  onClick={(e) => {
                    e.stopPropagation();
                    go(1);
                  }}
                  aria-label="Next media"
                >
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
}

export default FieldOps;
